import * as React from 'react';
import {
  DataGridPremium,
  GridToolbar,
  GridRowsProp,
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
  GridColumnVisibilityModel,
  GridPaginationModel,
} from '@mui/x-data-grid-premium';
import { Chip, Link, Paper } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';

import { DateTime } from 'luxon';

import { EventFormStatus, SafetyEventReport } from '../../core/api/clients';
import { Preference, usePreferences } from '../../core/context/PreferencesContext';

function SafetyEventReportDataGrid({ data, reportType, readOnly, areSubmissions, pageSize = 25 }: {
  data: UseQueryResult<SafetyEventReport[], unknown>,
  reportType: string,
  readOnly?: boolean,
  areSubmissions?: boolean,
  pageSize?: number,
}) {
  const [dataRows, setDataRows] = React.useState<GridRowsProp>([]);
  const [page, setPage] = React.useState<GridPaginationModel>({ page: 0, pageSize });
  const [hoverRow, setHoveredRow] = React.useState<GridValidRowModel | undefined>(undefined);
  const { dataGridDensity, updatePreference, safetyEventReportGridColumns } = usePreferences();

  let action = 'approve';
  if (readOnly) action = 'read';
  if (areSubmissions) action = 'review';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Submitted': return 'warning';
      case 'Approved': return 'success';
      case 'Final': return 'primary';
      case 'Deleted': return 'error';
      default: return undefined;
    }
  };

  const handlePopperClose = () => {
    setHoveredRow(undefined);
  };
  const displayChecked = (params: GridRenderCellParams<any, any, any>): string => {
    if (params.value === 'Yes' || params.value === true) {
      return 'YES';
    }

    if (params.value === undefined || params.value === 'NotApplicable' || params.value === 'Unknown') {
      return 'UNKNOWN';
    }

    return 'NO';
  };
  const columns: GridColDef[] = [
    { field: 'id' },
    {
      field: 'eventTitle',
      headerName: 'Event Title',
      renderCell: (params: GridRenderCellParams) => (
        <Link
          href={`form/${reportType}/${params.id}/${params.row.status === 'Deleted' ? 'view' : action}`}
          underline="hover"
        >
          {params.value}
        </Link>
      ),
      flex: 1.5,
    },
    { field: 'eventType', headerName: 'Event Type', flex: 1 },
    { field: 'eventType2', headerName: 'Event Type 2', flex: 1 },
    { field: 'eventType3', headerName: 'Event Type 3', flex: 1 },
    { field: 'aircraftNumber', headerName: 'AC', flex: 0.5 },
    { field: 'flightNumber', headerName: 'Flight', flex: 0.5 },
    { field: 'departureAirport', headerName: 'DEP', flex: 0.5 },
    { field: 'scheduledArrivalAirport', headerName: 'ARR', flex: 0.5 },
    {
      field: 'eventDate',
      type: 'date',
      headerName: 'Event Date',
      valueGetter: (value) => DateTime.fromISO(value).toJSDate(),
      renderCell: (params: GridRenderCellParams) => DateTime.fromJSDate(params.value).toLocaleString(DateTime.DATE_SHORT),
      flex: 0.75,
    },
    { field: 'employeeName', headerName: 'Emp Name', flex: 1 },
    {
      field: 'lastModifiedOn',
      type: 'dateTime',
      headerName: 'Modified',
      flex: 1,
      valueGetter: (value) => DateTime.fromISO(value).toJSDate(),
      renderCell: (params: GridRenderCellParams) => DateTime.fromJSDate(params.value).toLocaleString(DateTime.DATETIME_SHORT),
    },
    { field: 'lastModifiedName', headerName: 'Modified By', flex: 1 },
    { field: 'emergencyDeclared',
      headerName: 'Emergency',
      flex: 0.5,
      renderCell: (params) => (displayChecked(params)) },
    { field: 'narrative', headerName: 'Narrative', flex: 1 },
    { field: 'eventReviewComments', headerName: 'Event Review', flex: 1, renderCell: (params: GridRenderCellParams) => (params.value && params.value.length > 0 ? 'YES' : 'NO') },
  ];

  const updDataRows = (newData: SafetyEventReport[]) => {
    setDataRows(newData.slice().sort((a, b) => {
      if (a.status !== b.status) {
        switch (a.status) {
          case EventFormStatus.Submitted:
            return -1;
          case EventFormStatus.Approved:
            return 1;
          case EventFormStatus.Final:
            return 2;
          case EventFormStatus.Deleted:
            return 3;
          default:
        }
      }

      return DateTime.fromISO(b.eventDate!).toMillis() - DateTime.fromISO(a.eventDate!).toMillis();
    }));
  };
  React.useEffect(() => {
    const areObjectsEqual = JSON.stringify(safetyEventReportGridColumns) === JSON.stringify({});
    if (!safetyEventReportGridColumns || areObjectsEqual) {
      updatePreference(Preference.SafetyEventReportGridColumns, {
        id: false,
        eventType: false,
        eventType3: false,
        narrative: false,
        employeeName: (areSubmissions && reportType !== 'dispatch-event') ?? true,
      });
    }
  }, []);
  React.useEffect(() => {
    if (data.status === 'success' && data.data !== undefined) {
      updDataRows(data.data);
    }
    return () => {
      setDataRows([]);
    };
  }, [data]);

  return (
    <React.Fragment>
      <DataGridPremium
        density={dataGridDensity}
        onDensityChange={(density) => updatePreference(Preference.DataGridDensity, density)}
        autoHeight
        paginationModel={page}
        pagination
        onPaginationModelChange={(newPage) => setPage(newPage)}
        rows={dataRows}
        columnVisibilityModel={safetyEventReportGridColumns}
        onColumnVisibilityModelChange={(model) => {
          updatePreference(Preference.SafetyEventReportGridColumns, model);
        }}
        columns={columns}
        rowHeight={35}
        pageSizeOptions={[10, 25, 50]}
        slots={{
          toolbar: GridToolbar,
        }}
        slotProps={{
          row: {
            onMouseOver: (event: any): void => {
              const { id } = event.currentTarget.dataset;

              if (hoverRow === undefined || hoverRow.id.toString() !== id) {
                const row = dataRows.find((x) => x.id.toString() === id);
                setHoveredRow(row);
              }
            },
            onMouseLeave: handlePopperClose,
          },
        }}
      />
      {
        (hoverRow !== undefined) && (hoverRow.status === EventFormStatus.Deleted)
        && (
          <Paper sx={{ backgroundColor: '#FFCCCB' }}>
            <span style={{ marginRight: 10, marginLeft: 10, fontWeight: 'bold', color: '#484848' }}>Reason Deleted:</span>
            {hoverRow === undefined ? '' : hoverRow.reasonDeleted}
          </Paper>
        )
      }

    </React.Fragment>
  );
}

export default SafetyEventReportDataGrid;
